import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

export const STATUT_LABELS = {
  en_attente: "En attente",
  en_cours: "En cours",
  remis: "Remis",
  remis_livreur: "Remis au livreur",
  coupon_emis: "Coupon émis",
  anomalie: "Anomalie",
  annule: "Annulé",
  annule_mineur: "Annulé (mineur)",
  expire: "Expiré"
};

export const STATUT_COLORS = {
  en_attente: "bg-amber-100 text-amber-700 border-amber-300",
  en_cours: "bg-blue-100 text-blue-700 border-blue-300",
  remis: "bg-emerald-100 text-emerald-700 border-emerald-300",
  remis_livreur: "bg-violet-100 text-violet-700 border-violet-300",
  coupon_emis: "bg-cyan-100 text-cyan-700 border-cyan-300",
  anomalie: "bg-red-100 text-red-700 border-red-300",
  annule: "bg-slate-100 text-slate-500 border-slate-300",
  annule_mineur: "bg-red-100 text-red-700 border-red-400 font-semibold",
  expire: "bg-rose-100 text-rose-700 border-rose-300"
};

/**
 * Renvoie le badge à afficher pour un dossier en tenant compte des flux spéciaux :
 * - Procuration enregistrée par le BO (l'agent mobile n'a pas encore démarré) → badge violet
 *   "En attente • Procuration". Dès que l'agent fait la vérification, le statut passe en_cours
 *   et on affiche un badge bleu classique "En cours" comme une remise normale.
 * - Livraison intérieur lancée mais pas encore récupérée → badge violet "En attente • Intérieur".
 * - Sinon → badge classique selon le statut.
 */
export function getDossierBadge(dossier) {
  const statut = dossier?.statut;
  const proc = dossier?.remise_procuration;
  const procEnregistree = proc?.active && proc?.etat === 'enregistree';
  const livraisonActive = dossier?.livraison_interieur && !['remis', 'remis_livreur'].includes(statut);

  // Procuration enregistrée → tant que l'agent mobile n'a pas démarré la vérification
  if (statut === 'en_attente' && procEnregistree) {
    return {
      className: "bg-violet-100 text-violet-700 border-violet-300",
      label: "En attente • Procuration",
    };
  }

  // Livraison intérieur → tant que le gagnant n'a pas retiré au point relais
  if (['en_attente', 'en_cours'].includes(statut) && livraisonActive) {
    return {
      className: "bg-violet-100 text-violet-700 border-violet-300",
      label: "En attente • Intérieur",
    };
  }

  return {
    className: STATUT_COLORS[statut] || 'bg-slate-100 text-slate-700',
    label: STATUT_LABELS[statut] || statut,
  };
}

export const MODE_REMISE_LABELS = {
  magasin_kadolo: "Magasin KADOLOGISTIQUE",
  point_retrait: "Point de retrait"
};

export const ROLE_LABELS = {
  admin_kadolo: "Admin KADOLOGISTIQUE",
  operator_kadolo: "Opérateur KADOLOGISTIQUE",
  supervisor: "Superviseur",
  remettant_magasin: "Remettant Magasin KADOLOGISTIQUE",
  remettant_point_retrait: "Remettant Point de Retrait",
  auditeur: "Auditeur"
};

export const TYPE_ANOMALIE_LABELS = {
  id_ticket_incorrect: "ID Ticket incorrect",
  telephone_incorrect: "Téléphone incorrect",
  identite_non_conforme: "Identité non conforme",
  gagnant_non_present: "Gagnant non présent",
  lot_indisponible: "Lot indisponible",
  lot_deja_remis: "Lot déjà remis",
  lot_endommage: "Lot endommagé",
  erreur_site_remise: "Erreur site de remise",
  refus_remise: "Refus de remise",
  fraude_suspectee: "Fraude suspectée",
  dossier_incomplet: "Dossier de remise incomplet",
  autre: "Autre"
};

export const TYPE_SITE_LABELS = {
  magasin_kadolo: "Magasin KADOLOGISTIQUE",
  point_retrait: "Point de retrait"
};

export const TYPE_LOT_LABELS = {
  standard: "Standard",
  hors_format: "Hors format"
};

export const STATUT_LOT_LABELS = {
  disponible: "Disponible",
  reserve: "Réservé",
  remis: "Remis",
  indisponible: "Indisponible",
  annule: "Annulé"
};

export const TYPE_PREUVE_LABELS = {
  photo_piece_identite: "Photo pièce d'identité",
  photo_gagnant: "Photo du gagnant",
  photo_gagnant_lot: "Photo gagnant avec lot",
  photo_lot: "Photo du lot",
  photo_document_retrait: "Photo document de retrait",
  signature: "Signature",
  commentaire: "Commentaire"
};

export const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatDateShort = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export const formatCurrency = (value) => {
  if (!value && value !== 0) return "-";
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'XOF',
    minimumFractionDigits: 0
  }).format(value);
};

export const isAdmin = (user) => {
  return user?.role === 'admin_kadolo' || user?.role === 'operator_kadolo';
};

export const isMobileUser = (user) => {
  return ['remettant_magasin', 'remettant_point_retrait'].includes(user?.role);
};
